import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { ChevronUp, ChevronDown, ChevronRight, Trash2, Loader2, User, Paperclip } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'
import * as Query from '../../../api/gql/query'
import AgendaEntriesSection from './AgendaEntriesSection'
import MeetingFileUpload from './MeetingFileUpload'
import { MeetingFile } from './MeetingFileRow'

interface AgendaItem {
  id: string
  orderIndex: number
  title: string
  responsible?: { id: string; name: string } | null
  files?: MeetingFile[]
  subItems: Array<{
    id: string
    orderIndex: number
    title: string
    notes?: string | null
    kind: 'NOTE' | 'DECISION' | 'RISK' | 'TASK'
    status: string
    dueDate?: string | null
    assignedTo?: { id: string; name: string } | null
  }>
  actionItems: Array<{
    id: string
    title: string
    description?: string | null
    status: string
    priority: string
    dueDate?: string | null
    assignedTo?: { id: string; name: string } | null
    agendaOrderIndex?: number | null
    agendaItem?: { id: string; title: string } | null
    linkedSnag?: { id: string } | null
    linkedDoc?: { id: string } | null
  }>
}

interface Props {
  item: AgendaItem
  // Owning meeting — attachments are stored under meetings/{meetingId} in S3.
  meetingId: string
  users: { id: string; name: string }[]
  onSavingChange?: (saving: boolean) => void
  canMoveUp?: boolean
  canMoveDown?: boolean
  onMoveUp?: () => void
  onMoveDown?: () => void
  onDelete?: () => void
  structuralSaving?: boolean
  deleting?: boolean
  // MEETINGS.EDIT — gates the responsible-person select (updateAgendaItem) and is
  // threaded down to the entries editor.
  canEdit: boolean
  // MEETINGS.DELETE — pass-through for entries editor (see AgendaEntriesSection).
  canDelete: boolean
}

const AgendaItemEditor: React.FC<Props> = ({
  item,
  meetingId,
  users,
  onSavingChange,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onDelete,
  structuralSaving,
  deleting,
  canEdit,
  canDelete,
}) => {
  const { t } = useTranslation()
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id
  const [subSaving, setSubSaving] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const files: MeetingFile[] = item.files || []
  const [filesOpen, setFilesOpen] = useState(files.length > 0)

  const [updateAgendaItem, { loading: savingItem }] = useMutation(Mutation.UPDATE_AGENDA_ITEM)
  const [setAgendaItemFiles, { loading: savingFiles }] = useMutation(
    Mutation.SET_AGENDA_ITEM_FILES
  )

  const anySaving = savingItem || subSaving || savingFiles

  useEffect(() => {
    onSavingChange?.(anySaving)
  }, [anySaving, onSavingChange])

  const changeResponsible = async (responsibleId: string | null) => {
    if ((item.responsible?.id || null) === responsibleId) return
    try {
      await updateAgendaItem({
        variables: {
          id: item.id,
          input: {
            title: item.title,
            responsibleId,
            orderIndex: item.orderIndex,
          },
        },
      })
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  return (
    <div
      className={cn(
        'border rounded-lg bg-card overflow-hidden transition-opacity',
        deleting && 'opacity-50 pointer-events-none'
      )}
    >
      <div
        className={cn(
          'px-4 py-3 bg-muted/30 flex items-center justify-between gap-3',
          !collapsed && 'border-b'
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="min-w-0 flex-1 flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
        >
          <ChevronRight
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              !collapsed && 'rotate-90'
            )}
          />
          <div className="min-w-0">
            <h4 className="font-medium truncate">{item.title}</h4>
            {item.responsible && (
              <p className="text-xs text-muted-foreground">
                {item.responsible.name}
              </p>
            )}
          </div>
        </button>
        {files.length > 0 && (
          <span
            className="flex items-center gap-1 shrink-0 text-xs text-muted-foreground"
            title={t('Meeting_agenda_attachments')}
          >
            <Paperclip className="h-3.5 w-3.5" />
            {files.length}
          </span>
        )}
        {(onMoveUp || onMoveDown || onDelete) && (
          <div className="flex items-center gap-1 shrink-0">
            {onMoveUp && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={!canMoveUp || structuralSaving}
                onClick={onMoveUp}
                title={t('Meeting_agenda_move_up')}
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            )}
            {onMoveDown && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={!canMoveDown || structuralSaving}
                onClick={onMoveDown}
                title={t('Meeting_agenda_move_down')}
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                disabled={structuralSaving}
                onClick={onDelete}
                title={t('Common_delete')}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground shrink-0" />
            <Select
              value={item.responsible?.id || '__none__'}
              onValueChange={(v) => changeResponsible(v === '__none__' ? null : v)}
              disabled={savingItem || !canEdit}
            >
              <SelectTrigger className="h-8 min-w-[200px] text-sm">
                <SelectValue
                  placeholder={t('Agenda_responsible_placeholder', {
                    defaultValue: 'Vyberte řešitele',
                  })}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <AgendaEntriesSection
            agendaItemId={item.id}
            subItems={item.subItems}
            actionItems={item.actionItems}
            users={users}
            onSavingChange={setSubSaving}
            canEdit={canEdit}
            canDelete={canDelete}
          />

          {(canEdit || files.length > 0) && (
            <div className="pt-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                onClick={() => setFilesOpen((v) => !v)}
                aria-expanded={filesOpen}
              >
                <Paperclip className="h-3.5 w-3.5" />
                {t('Meeting_agenda_attachments')}
                {files.length > 0 && ` (${files.length})`}
                <ChevronDown
                  className={cn('h-3.5 w-3.5 transition-transform', filesOpen && 'rotate-180')}
                />
              </Button>

              {filesOpen && (
                <div className="pt-2">
                  <MeetingFileUpload
                    meetingId={meetingId}
                    projectId={projectId}
                    files={files}
                    onFilesChange={(fileIds) =>
                      setAgendaItemFiles({ variables: { id: item.id, fileIds } })
                    }
                    disabled={!canEdit}
                    compact
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AgendaItemEditor
